import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { tokens } from '../../theme/tokens.css';
import { beginSliderDragCursor, endSliderDragCursor } from '../slider-drag-cursor';

/**
 * Range Slider - A slider control for numeric values
 *
 * @element range-slider
 * @fires input - Fired continuously while dragging, detail contains { value: number }
 * @fires change - Fired when drag ends or value is set, detail contains { value: number }
 */
@customElement('range-slider')
export class RangeSlider extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
      }

      .slider-container {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .slider-track {
        flex: 1;
        position: relative;
        height: 6px;
        background-color: var(--color-slider-track);
        border-radius: var(--radius-xs);
        cursor: pointer;
        /* Own the drag gesture — without this iOS pans the menu scroll
           instead of moving the thumb. */
        touch-action: none;
      }

      .slider-fill {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        background-color: var(--color-btn-play);
        border-radius: var(--radius-xs);
        pointer-events: none;
      }

      .slider-thumb {
        position: absolute;
        top: 50%;
        width: 16px;
        height: 16px;
        background-color: var(--color-slider-thumb);
        border-radius: 50%;
        box-shadow: var(--shadow-control);
        transform: translate(-50%, -50%);
        cursor: grab;
        transition: transform 0.1s ease;
        touch-action: none;
        -webkit-tap-highlight-color: transparent;
      }

      /* Hover-only — gated so iOS sticky-hover doesn't leave the thumb
         enlarged after a touch. */
      @media (hover: hover) {
        .slider-thumb:hover {
          transform: translate(-50%, -50%) scale(1.1);
        }
      }

      .slider-thumb:active {
        cursor: grabbing;
        transform: translate(-50%, -50%) scale(1.15);
      }

      /* Fatter invisible hit-zone on touch devices — a 16px thumb is below
         finger-target size; this pads it to ~44px without visual change. */
      @media (hover: none) {
        .slider-thumb::after {
          content: '';
          position: absolute;
          inset: -14px;
        }
      }

      .slider-value {
        min-width: 48px;
        text-align: right;
        font-size: var(--font-size-sm);
        font-weight: 500;
        color: var(--color-text-white);
      }

      .reset-button {
        position: relative;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--color-bg-btn-ghost);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        opacity: 0;
        visibility: hidden;
        -webkit-tap-highlight-color: transparent;
        transition:
          opacity 0.2s ease,
          background-color 0.2s ease,
          /* Held until the fade finishes, otherwise visibility flips instantly
             on the way out and the opacity transition is never seen. */
          visibility 0s linear 0.2s;
      }

      .reset-button.visible {
        opacity: 1;
        visibility: visible;
        /* No delay on the way in — appear at once, then fade up. */
        transition-delay: 0s;
      }

      /* Hover-only — gated so iOS sticky-hover doesn't leave the button lit
         after a tap. */
      @media (hover: hover) {
        .reset-button:hover {
          background: var(--color-bg-btn-ghost-hover);
        }
      }

      .reset-button:active {
        background: var(--color-bg-btn-ghost-active);
      }

      /* Pad the 24px button to finger-target size on touch devices,
         matching the thumb's invisible hit-zone. */
      @media (hover: none) {
        .reset-button::after {
          content: '';
          position: absolute;
          inset: -10px;
        }
      }

      .reset-icon {
        width: 14px;
        height: 14px;
        background-color: var(--color-text-secondary);
        mask-image: url('/vanilla/ui/menu/rotate-ccw.svg');
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-image: url('/vanilla/ui/menu/rotate-ccw.svg');
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      @media (hover: hover) {
        .reset-button:hover .reset-icon {
          background-color: var(--color-text-white);
        }
      }
    `,
  ];

  /** Current value */
  @property({ type: Number })
  value = 1;

  /** Minimum value */
  @property({ type: Number })
  min = 0;

  /** Maximum value */
  @property({ type: Number })
  max = 1;

  /** Step increment */
  @property({ type: Number })
  step = 0.1;

  /** Default value (shows reset button when different) */
  @property({ type: Number, attribute: 'default-value' })
  defaultValue: number | null = null;

  /** Format string for display (use {value} as placeholder) */
  @property({ type: String })
  format = '{value}';

  private isDragging = false;
  private justFinishedDragging = false;
  /** Pointer that started the active drag — guards against a second finger
   *  hijacking the document-level move/up listeners mid-drag. */
  private activePointerId: number | null = null;
  /** Teardown for the in-flight drag, held so disconnectedCallback can run
   *  it — a panel closing mid-drag must not leave document listeners or the
   *  drag cursor behind. */
  private dragCleanup: (() => void) | null = null;

  /** @override Tears down any in-flight drag when the component unmounts */
  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.dragCleanup?.();
  }

  // --- Helper methods for numeric precision ---

  private clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, n));
  }

  /** Determine decimal places needed for step (e.g. 0.1 → 1, 0.01 → 2, 5 → 0) */
  private stepDecimals(): number {
    const s = this.step;
    if (!Number.isFinite(s) || s <= 0) return 0;
    const str = s.toString();
    const dotIndex = str.indexOf('.');
    if (dotIndex === -1) return 0;
    return str.length - dotIndex - 1;
  }

  /** Round to the precision required by the step value */
  private roundToStepPrecision(n: number): number {
    const d = this.stepDecimals();
    const factor = Math.pow(10, d);
    return Math.round(n * factor) / factor;
  }

  /** The one canonical value-normalization path: snap to the step grid
   *  (relative to min), clamp to bounds, round to step precision.
   *  Returns null when min/max/step are misconfigured. */
  private normalizeValue(value: number): number | null {
    const range = this.max - this.min;
    if (!Number.isFinite(range) || range <= 0) return null;
    if (!Number.isFinite(this.step) || this.step <= 0) return null;

    const steppedValue = this.min + Math.round((value - this.min) / this.step) * this.step;
    return this.roundToStepPrecision(this.clamp(steppedValue, this.min, this.max));
  }

  private isAtDefault(): boolean {
    if (this.defaultValue === null) return true;
    const roundedValue = this.roundToStepPrecision(this.value);
    const roundedDefault = this.roundToStepPrecision(this.defaultValue);
    return roundedValue === roundedDefault;
  }

  private handleReset = (e: MouseEvent): void => {
    e.stopPropagation();
    if (this.defaultValue !== null && !this.isAtDefault()) {
      // Run the default through the same snap/clamp path as drag updates so
      // an off-step or out-of-bounds defaultValue can't sneak past them.
      const nextValue = this.normalizeValue(this.defaultValue);
      if (nextValue === null || nextValue === this.value) return;
      this.value = nextValue;

      // Emit both input and change for reset
      this.dispatchEvent(
        new CustomEvent('input', {
          detail: { value: this.value },
          bubbles: true,
          composed: true,
        }),
      );
      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.value },
          bubbles: true,
          composed: true,
        }),
      );
    }
  };

  private getPercentage(): number {
    const range = this.max - this.min;
    if (!Number.isFinite(range) || range <= 0) return 0;
    return this.clamp(((this.value - this.min) / range) * 100, 0, 100);
  }

  private getFormattedValue(): string {
    const rounded = this.roundToStepPrecision(this.value);
    return this.format.replace('{value}', rounded.toString());
  }

  private updateValueFromPosition(clientX: number, rect: DOMRect, emitChange = false): void {
    if (!Number.isFinite(rect.width) || rect.width <= 0) return;

    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const rawValue = this.min + percentage * (this.max - this.min);

    const newValue = this.normalizeValue(rawValue);
    if (newValue === null) return;

    if (newValue !== this.value) {
      this.value = newValue;

      // Emit 'input' during drag for visual feedback
      this.dispatchEvent(
        new CustomEvent('input', {
          detail: { value: this.value },
          bubbles: true,
          composed: true,
        }),
      );

      // Emit 'change' only when requested (on mouseup/click)
      if (emitChange) {
        this.dispatchEvent(
          new CustomEvent('change', {
            detail: { value: this.value },
            bubbles: true,
            composed: true,
          }),
        );
      }
    }
  }

  private handleTrackClick = (e: MouseEvent): void => {
    e.stopPropagation();
    // Ignore clicks right after dragging, and a second finger tapping the
    // track while the first is still mid-drag.
    if (this.isDragging || this.justFinishedDragging) {
      return;
    }
    const track = e.currentTarget as HTMLElement;
    const rect = track.getBoundingClientRect();
    this.updateValueFromPosition(e.clientX, rect, true); // Emit both input and change
  };

  private handleThumbPointerDown = (e: PointerEvent): void => {
    // Primary pointer / left button only; never while a drag is active.
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0) || this.activePointerId !== null) return;
    e.stopPropagation();
    e.preventDefault();

    const thumb = e.currentTarget as HTMLElement;
    const track = thumb.parentElement!;

    // Capture so move/up keep arriving even when the finger leaves the thumb.
    // Claim it before touching any state: setPointerCapture throws
    // NotFoundError if the pointer is no longer active, and a throw part-way
    // through setup would strand isDragging/activePointerId with no listeners
    // and no cleanup registered — after which the guard above rejects every
    // future press and the slider is dead until it remounts.
    try {
      thumb.setPointerCapture(e.pointerId);
    } catch {
      return;
    }

    this.isDragging = true;
    this.justFinishedDragging = false;
    this.activePointerId = e.pointerId;
    // Snapshot for the no-pointerup teardown paths (cancel/blur/unmount) —
    // they commit only if the drag actually moved the value.
    const startValue = this.value;

    beginSliderDragCursor();

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== this.activePointerId) return;
      moveEvent.stopPropagation();
      moveEvent.preventDefault();
      if (this.isDragging) {
        // Recompute rect each move to handle scrolling/resizing during drag
        const rect = track.getBoundingClientRect();
        this.updateValueFromPosition(moveEvent.clientX, rect);
      }
    };

    // Block any click events that fire after pointerup
    const handleClick = (clickEvent: MouseEvent) => {
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
    };

    // Cleanup function for guaranteed teardown. Deliberately does NOT touch
    // justFinishedDragging — only the pointerup path sets it (to swallow the
    // click that follows a drag), and only its rAF resets it. Setting it here
    // left it stuck true forever when teardown came from blur, killing all
    // future track clicks.
    const cleanup = () => {
      this.isDragging = false;
      this.activePointerId = null;
      this.dragCleanup = null;
      endSliderDragCursor();
      document.removeEventListener('pointermove', handlePointerMove, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
      document.removeEventListener('pointercancel', handlePointerCancel, true);
      window.removeEventListener('blur', teardownAndCommit);
    };

    // Teardown for drags that end without a pointerup (cancel, blur,
    // unmount). The drag already previewed values via 'input' — commit them,
    // otherwise what the user sees silently diverges from what persists
    // (consumers preview on 'input' but save on 'change').
    const teardownAndCommit = () => {
      cleanup();
      if (this.value !== startValue) {
        this.dispatchEvent(
          new CustomEvent('change', {
            detail: { value: this.value },
            bubbles: true,
            composed: true,
          }),
        );
      }
    };
    this.dragCleanup = teardownAndCommit;

    // iOS fires pointercancel when the system steals the gesture.
    const handlePointerCancel = (cancelEvent: PointerEvent) => {
      if (cancelEvent.pointerId !== this.activePointerId) return;
      teardownAndCommit();
    };

    const handlePointerUp = (upEvent: PointerEvent) => {
      if (upEvent.pointerId !== this.activePointerId) return;
      upEvent.stopPropagation();
      upEvent.preventDefault();

      // Apply the release position first — a fast drag can coalesce the
      // final movement into the up event, leaving the last pointermove
      // value one step stale.
      this.updateValueFromPosition(upEvent.clientX, track.getBoundingClientRect());

      cleanup();
      this.justFinishedDragging = true;

      // Only when the drag actually moved the value — same guard the
      // cancel/blur path uses. A press-and-release on the thumb that never
      // moved would otherwise make consumers persist a value they already had.
      if (this.value !== startValue) {
        this.dispatchEvent(
          new CustomEvent('change', {
            detail: { value: this.value },
            bubbles: true,
            composed: true,
          }),
        );
      }

      // Temporarily block clicks to prevent backdrop click
      document.addEventListener('click', handleClick, true);
      requestAnimationFrame(() => {
        document.removeEventListener('click', handleClick, true);
        this.justFinishedDragging = false;
      });
    };

    // Use capture phase to intercept events before they reach other handlers.
    // The blur listener must NOT use capture: blur doesn't bubble but it does
    // capture, so a capturing window listener fires for every element blur in
    // the document — we only want the window itself losing focus (alt-tab).
    document.addEventListener('pointermove', handlePointerMove, true);
    document.addEventListener('pointerup', handlePointerUp, true);
    document.addEventListener('pointercancel', handlePointerCancel, true);
    window.addEventListener('blur', teardownAndCommit);
  };

  override render() {
    const percentage = this.getPercentage();
    const showReset = !this.isAtDefault();

    return html`
      <div class="slider-container">
        <div class="slider-track" @click=${this.handleTrackClick}>
          <div class="slider-fill" style="width: ${percentage}%"></div>
          <div class="slider-thumb" style="left: ${percentage}%" @pointerdown=${this.handleThumbPointerDown}></div>
        </div>
        <span class="slider-value">${this.getFormattedValue()}</span>
        <button
          type="button"
          class="reset-button ${showReset ? 'visible' : ''}"
          @click=${this.handleReset}
          title="Reset to default"
        >
          <div class="reset-icon"></div>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'range-slider': RangeSlider;
  }
}
